import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
  isRTL: boolean;
}

interface Translation {
  [key: string]: string;
}

interface Translations {
  en: Translation;
  ar: Translation;
}

const translations: Translations = {
  en: {
    // Header
    'header.title': 'Tabib',
    'header.subtitle': 'Healthcare Platform',
    'header.signIn': 'Sign In',
    'header.arabic': 'العربية',
    'header.english': 'English',

    // Landing Page
    'landing.hero.title': "Iraq's Comprehensive Healthcare Platform",
    'landing.hero.subtitle':
      'Secure medical record management, appointment scheduling, and healthcare coordination for patients, doctors, hospitals, and pharmacies across Iraq.',
    'landing.hero.getStarted': 'Get Started',

    // Features
    'features.title': 'Complete Healthcare Management',
    'features.subtitle':
      'Advanced medical document management with secure upload, categorization, and seamless coordination across Iraqi healthcare providers.',

    'features.records.title': 'Medical Records',
    'features.records.description':
      'Upload, categorize, and manage physical medical documents with OCR text extraction and secure cloud storage.',

    'features.appointments.title': 'Appointment Scheduling',
    'features.appointments.description':
      'Schedule appointments with healthcare providers, track upcoming visits, and join video consultations.',

    'features.medications.title': 'Medication Management',
    'features.medications.description':
      'Track prescriptions, monitor adherence rates, and coordinate with pharmacies for medication refills.',

    'features.multiUser.title': 'Multi-User Platform',
    'features.multiUser.description':
      'Role-based dashboards for patients, doctors, hospitals, pharmacies, and healthcare administrators.',

    'features.compliance.title': 'HIPAA Compliance',
    'features.compliance.description':
      'Enterprise-grade security with encryption at rest and in transit, audit trails, and compliance logging.',

    'features.monitoring.title': 'Health Monitoring',
    'features.monitoring.description':
      'Track vital signs, lab results, and wellness metrics with expandable health overview dashboards.',

    // User Types
    'userTypes.title': 'Built for All Healthcare Stakeholders',
    'userTypes.subtitle': 'Comprehensive platform serving the entire Iraqi healthcare ecosystem',
    'userTypes.patients': 'Patients',
    'userTypes.doctors': 'Doctors',
    'userTypes.hospitals': 'Hospitals',
    'userTypes.pharmacies': 'Pharmacies',
    'userTypes.healthServices': 'Health Services',
    'userTypes.systemAdmins': 'System Admins',

    // CTA
    'cta.title': "Join Iraq's Digital Healthcare Revolution",
    'cta.subtitle':
      'Start managing your health records digitally with secure, professional-grade tools designed for the Iraqi healthcare system.',
    'cta.signUp': 'Sign Up Now',

    // Footer
    'footer.description':
      "Iraq's comprehensive healthcare platform for secure medical record management.",
    'footer.platform': 'Platform',
    'footer.platform.dashboard': 'Patient Dashboard',
    'footer.platform.records': 'Medical Records',
    'footer.platform.appointments': 'Appointments',
    'footer.platform.medications': 'Medications',
    'footer.users': 'Users',
    'footer.support': 'Support',
    'footer.support.help': 'Help Center',
    'footer.support.contact': 'Contact Us',
    'footer.support.privacy': 'Privacy Policy',
    'footer.support.terms': 'Terms of Service',
    'footer.copyright': '© 2024 Tabib Healthcare Platform. All rights reserved.',

    // Dashboard
    'dashboard.welcome': 'Welcome back',
    'dashboard.healthOverview': 'Health Overview',
    'dashboard.viewAll': 'View All',
    'dashboard.medicalRecords': 'Medical Records',
    'dashboard.appointments': 'Appointments',
    'dashboard.medications': 'Medications',
    'dashboard.noRecords': 'No medical records uploaded yet',
    'dashboard.uploadFirst': 'Upload your first medical document to get started',
    'dashboard.uploadDocument': 'Upload Document',

    // Health Metrics
    'health.bloodPressure': 'Blood Pressure',
    'health.heartRate': 'Heart Rate',
    'health.weight': 'Weight',
    'health.glucose': 'Blood Glucose',
    'health.temperature': 'Temperature',
    'health.oxygen': 'Oxygen Saturation',
    'health.addMetric': 'Add New Metric',
    'health.lastUpdated': 'Last updated',
    'health.trend': 'Trend',
    'health.normal': 'Normal',
    'health.high': 'High',
    'health.low': 'Low',

    // Medical Records
    'records.title': 'Medical Records',
    'records.uploadNew': 'Upload New Document',
    'records.category': 'Category',
    'records.labResults': 'Lab Results',
    'records.xrays': 'X-rays',
    'records.prescriptions': 'Prescriptions',
    'records.reports': 'Medical Reports',
    'records.insurance': 'Insurance',
    'records.other': 'Other',
    'records.description': 'Description',
    'records.upload': 'Upload',
    'records.dropZone': 'Drop files here or click to upload',
    'records.supportedFormats': 'Supported formats: PDF, JPG, PNG, DICOM (max 10MB)',
    'records.unreadableText':
      "This document's text could not be read automatically (it appears to be a scanned image). The AI assistant cannot see its contents yet.",

    // Appointments
    'appointments.title': 'Appointments',
    'appointments.upcoming': 'Upcoming',
    'appointments.past': 'Past',
    'appointments.schedule': 'Schedule New',
    'appointments.with': 'with',
    'appointments.at': 'at',
    'appointments.status.scheduled': 'Scheduled',
    'appointments.status.completed': 'Completed',
    'appointments.status.cancelled': 'Cancelled',

    // Medications
    'medications.title': 'Current Medications',
    'medications.add': 'Add Medication',
    'medications.name': 'Medication Name',
    'medications.dosage': 'Dosage',
    'medications.frequency': 'Frequency',
    'medications.prescribedBy': 'Prescribed by',
    'medications.adherence': 'Adherence',
    'medications.taken': 'taken',
    'medications.missed': 'missed',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.view': 'View',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.today': 'Today',
    'common.yesterday': 'Yesterday',
    'common.thisWeek': 'This week',
    'common.thisMonth': 'This month',

    // Book Appointment
    bookAppointment: 'Book Appointment',
    providerType: 'Provider Type',
    selectProviderType: 'Select provider type',
    provider: 'Provider',
    selectProvider: 'Select a provider',
    selectProviderTypeFirst: 'Please select a provider type first',
    appointmentDate: 'Appointment Date',
    pickDate: 'Pick a date',
    appointmentTime: 'Appointment Time',
    selectTime: 'Select a time',
    reasonForVisit: 'Reason for Visit',
    reasonForVisitPlaceholder: 'Briefly describe your reason for this visit',
    additionalNotes: 'Additional Notes',
    additionalNotesPlaceholder: 'Any additional information for the doctor',
    booking: 'Booking...',
    appointmentBooked: 'Appointment Booked',
    appointmentBookedDesc: 'Your appointment has been scheduled successfully',
    appointmentBookError: 'Failed to book appointment',
    doctor: 'Doctor',
    hospital: 'Hospital',
    laboratory: 'Laboratory',
    healthServices: 'Health Services',
    cancel: 'Cancel',
    error: 'Error',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    actions: 'Actions',
    markAsTaken: 'Mark dose as taken',
    doseRecorded: 'Dose recorded',
    doseRecordedDesc: 'Adherence has been updated',
    medicationDeleted: 'Medication removed',
    medicationDeletedDesc: 'The medication has been removed from your list',
    medicationUpdated: 'Medication updated',
    medicationUpdatedDesc: 'Your changes have been saved',
    confirmDelete: 'Delete this medication?',
    confirmDeleteDesc:
      'This will permanently remove the medication from your list. You can add it again later.',
    editMedication: 'Edit medication',
    backToMedications: 'Back to medications',
    todayReminders: "Today's doses",
    expiringSoon: 'Expiring soon',
    activeMeds: 'Active medications',
    noMedications: 'No medications yet',
    noMedicationsDesc: 'Tap Add to start tracking your medications.',
    doctorName: 'Doctor/Provider Name',
    doctorNamePlaceholder: 'Enter doctor or provider name',
    specialty: 'Specialty',
    selectSpecialty: 'Select specialty',
    generalMedicine: 'General Medicine',
    cardiology: 'Cardiology',
    dermatology: 'Dermatology',
    pediatrics: 'Pediatrics',
    orthopedics: 'Orthopedics',
    gynecology: 'Gynecology',
    ophthalmology: 'Ophthalmology',
    ent: 'ENT (Ear, Nose, Throat)',
    psychiatry: 'Psychiatry',
    neurology: 'Neurology',
    appointmentType: 'Appointment Type',
    selectAppointmentType: 'Select appointment type',
    inPerson: 'In Person',
    videoCall: 'Video Call',
    phoneCall: 'Phone Call',
    reasonAndNotes: 'Reason for Visit & Notes',
    reasonAndNotesPlaceholder: 'Describe your reason for this visit and any additional information',
    reasonAndNotesDescription: 'Include your symptoms, concerns, or any information for the doctor',

    // Patient Profile
    patientProfile: 'Patient Profile',
    managePersonalInfo: 'Manage your personal and medical information',
    completeYourProfile: 'Complete your profile to get started',
    editProfile: 'Edit Profile',
    personalInformation: 'Personal Information',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Enter your full name',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    selectGender: 'Select gender',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    nationalId: 'National ID',
    preferredLanguage: 'Preferred Language',
    selectLanguage: 'Select language',
    contactInformation: 'Contact Information',
    phoneNumber: 'Phone Number',
    email: 'Email',
    address: 'Address',
    fullAddress: 'Enter your full address',
    city: 'City',
    postalCode: 'Postal Code',
    optional: 'Optional',
    emergencyContact: 'Emergency Contact',
    contactName: 'Contact Name',
    contactPhone: 'Contact Phone',
    relationship: 'Relationship',
    relationshipPlaceholder: 'e.g., Spouse, Parent, Sibling',
    medicalInformation: 'Medical Information',
    bloodType: 'Blood Type',
    selectBloodType: 'Select blood type',
    unknown: 'Unknown',
    allergies: 'Allergies',
    allergiesPlaceholder: 'List any known allergies',
    chronicConditions: 'Chronic Conditions',
    chronicConditionsPlaceholder: 'List any chronic conditions',
    currentMedications: 'Current Medications',
    medicationsPlaceholder: 'List current medications',
    insuranceInformation: 'Insurance Information',
    insuranceProvider: 'Insurance Provider',
    insuranceProviderPlaceholder: 'Enter insurance provider name',
    policyNumber: 'Policy Number',
    policyNumberPlaceholder: 'Enter policy number',
    saving: 'Saving...',
    updateProfile: 'Update Profile',
    createProfile: 'Create Profile',
    profileUpdated: 'Profile Updated',
    patientProfileUpdatedDesc: 'Your profile has been updated successfully',
    profileUpdateError: 'Failed to update profile',

    // Telemedicine
    'telemedicine.title': 'Video Consultations',
    'telemedicine.subtitle': 'Manage remote medical consultation sessions',
    'telemedicine.bookNew': 'Book New Consultation',
    'telemedicine.backToDashboard': '← Back to Dashboard',
    'telemedicine.scheduled': 'Consultation Scheduled',
    'telemedicine.scheduledDesc': 'Video consultation has been scheduled successfully',
    'telemedicine.upcomingSessions': 'Upcoming Sessions',
    'telemedicine.completedSessions': 'Completed Sessions',
    'telemedicine.avgRating': 'Average Rating',
    'telemedicine.avgDuration': 'Average Duration',
    'telemedicine.noUpcoming': 'No Upcoming Sessions',
    'telemedicine.noUpcomingPatient': "You haven't booked any consultations yet",
    'telemedicine.noUpcomingProvider': 'No consultations booked with you',
    'telemedicine.noHistory': 'No Session History',
    'telemedicine.noHistoryDesc': "You haven't completed any video consultations yet",
    'telemedicine.room': 'Room',
    'telemedicine.duration': 'Duration',
    'telemedicine.minutes': 'minutes',
    'telemedicine.withDoctor': 'With Doctor',
    'telemedicine.withPatient': 'With Patient',
    'telemedicine.joinSession': 'Join Session',
    'telemedicine.waiting': 'Waiting',
    'telemedicine.details': 'Details',
    'telemedicine.rating': 'Rating',
    'telemedicine.status.waiting': 'Waiting',
    'telemedicine.status.active': 'Active',
    'telemedicine.status.ended': 'Ended',
    'telemedicine.status.failed': 'Failed',

    // Medication form (flat keys — `add-medication.tsx` calls t('dosage'),
    // not t('medications.dosage'). The flat aliases below are duplicated
    // intentionally; removing them re-introduces the bug where the form
    // rendered raw key names like "dosagePlaceholder" as visible text.)
    addMedication: 'Add Medication',
    adding: 'Adding...',
    medicationName: 'Medication Name',
    medicationNamePlaceholder: 'e.g. Lisinopril',
    dosage: 'Dosage',
    dosagePlaceholder: 'e.g. 10 mg',
    dosageHelp: 'Include the unit (mg, ml, IU, etc.)',
    frequency: 'Frequency',
    selectFrequency: 'Select frequency',
    route: 'Route',
    selectRoute: 'Select route',
    startDate: 'Start Date',
    endDate: 'End Date',
    endDateOptional: 'Optional — leave blank for ongoing medications',
    prescribedBy: 'Prescribed By',
    prescribedByPlaceholder: 'Doctor or provider name',
    purpose: 'Purpose',
    purposePlaceholder: 'e.g. Blood pressure',
    instructions: 'Instructions',
    instructionsPlaceholder: 'e.g. Take with food',
    sideEffects: 'Side Effects',
    sideEffectsPlaceholder: 'List any side effects you have noticed',
    activeMedication: 'Active medication',
    activeMedicationDesc: 'Turn off to hide this medication from your active list',
    medicationAdded: 'Medication added',
    medicationAddedDesc: 'The medication has been added to your list',
    medicationAddError: 'Failed to add medication',

    // Safety-conflict confirm dialog (window.confirm today; the
    // confirmButton/cancelButton keys are queued for the future
    // swap-to-AlertDialog branch — see WORKLOG > Open questions).
    'medications.safetyConflict.title': 'Safety Conflict Detected',
    'medications.safetyConflict.body': 'A serious safety conflict was detected. Continue anyway?',
    'medications.safetyConflict.confirmButton': 'Continue Anyway',
    'medications.safetyConflict.cancelButton': 'Cancel',
    'medications.interactions.coverageDisclaimer':
      'Interaction coverage is non-exhaustive and limited to our current dataset. This check is not a substitute for pharmacist or clinician judgment.',
    'medications.interactions.emptyTitle': 'No alerts in the current dataset',
    'medications.interactions.emptyDescription':
      'No interactions or allergy conflicts were found in our current dataset. This does not confirm that the medication is safe.',

    // Frequency options
    onceDaily: 'Once daily',
    twiceDaily: 'Twice daily',
    threeTimesDaily: 'Three times daily',
    fourTimesDaily: 'Four times daily',
    every6Hours: 'Every 6 hours',
    every8Hours: 'Every 8 hours',
    every12Hours: 'Every 12 hours',
    asNeeded: 'As needed',
    weekly: 'Weekly',
    monthly: 'Monthly',

    // Route options
    oral: 'Oral',
    injection: 'Injection',
    topical: 'Topical',
    inhalation: 'Inhalation',
    rectal: 'Rectal',
    sublingual: 'Sublingual',
    transdermal: 'Transdermal',
    nasal: 'Nasal',
    ophthalmic: 'Ophthalmic (eye)',
    otic: 'Otic (ear)',

    // Provider search (keys not already defined elsewhere in this block)
    findHealthcareProviders: 'Find Healthcare Providers',
    searchProviderDescription: 'Search for doctors, hospitals, pharmacies, and labs across Iraq',
    searchByNameOrSpecialty: 'Search by name or specialty',
    allTypes: 'All Types',
    allCities: 'All Cities',
    allSpecializations: 'All Specializations',
    specialization: 'Specialization',
    pharmacy: 'Pharmacy',
    acceptsInsurance: 'Accepts Insurance',
    verified: 'Verified',
    noProvidersFound: 'No providers found',
    errorLoadingProviders: 'Failed to load providers',

    // Shared page chrome
    'common.backHome': 'Back to home',

    // 404
    'notFound.title': 'We can’t find that page',
    'notFound.body':
      'The link may be out of date, or the address may have been typed slightly differently. Nothing has gone wrong with your account.',
    'notFound.home': 'Go to home page',
    'notFound.help': 'Visit Help Center',

    // Sign-in and registration (/login, /register)
    'auth.login.title': 'Welcome back',
    'auth.login.subtitle': 'Sign in to your Tabib account',
    'auth.register.title': 'Create your account',
    'auth.register.subtitle': 'Join Tabib to manage your health records',
    'auth.field.firstName': 'First name',
    'auth.field.lastName': 'Last name',
    'auth.field.email': 'Email',
    'auth.field.password': 'Password',
    'auth.field.confirmPassword': 'Confirm password',
    'auth.action.signIn': 'Sign in',
    'auth.action.createAccount': 'Create account',
    'auth.action.biometric': 'Sign in with biometrics',
    'auth.divider.or': 'or',
    'auth.prompt.noAccount': "Don't have an account?",
    'auth.prompt.createOne': 'Create one',
    'auth.prompt.haveAccount': 'Already have an account?',
    'auth.error.invalidCredentials': 'Invalid email or password',
    'auth.error.passwordMismatch': "Passwords don't match",
    'auth.error.consentRequired':
      'You must accept the Privacy Policy and Terms of Service to continue',
    'auth.error.registrationFailed': 'Registration failed. Please try again.',
    'auth.error.biometricUnavailable': 'No registered biometric credentials',
    'auth.error.biometricFailed': 'Biometric authentication failed',

    // Multi-factor challenge
    'auth.mfa.title': 'Verify your identity',
    'auth.mfa.hint.totp': 'Enter the 6-digit code from your authenticator app.',
    'auth.mfa.hint.sms': 'We sent a code to {phone}.',
    'auth.mfa.hint.smsFallback': 'your phone',
    'auth.mfa.hint.email': 'We sent a code to your email.',
    'auth.mfa.hint.default': 'Enter your verification code.',
    'auth.mfa.codePlaceholder': 'Enter code',
    'auth.mfa.verify': 'Verify',
    'auth.mfa.resend': 'Resend code',
    'auth.mfa.lostAccess':
      'Lost access? Use a backup code in the same field above, or contact your administrator.',
    'auth.mfa.error.invalidCode': 'Invalid code',
    'auth.mfa.error.sendFailed': 'Failed to send challenge',

    // Help Center (/help)
    'help.title': 'Help Center',
    'help.subtitle': 'Answers to the questions we are asked most, and where to go next.',
    'help.faq.title': 'Frequently asked questions',
    'help.faq.account.q': 'How do I create an account?',
    'help.faq.account.a':
      'Choose "Get Started" on the home page to register as a patient. Doctors, hospitals, pharmacies, labs, and health services register through "Join as Provider", which adds a credential-verification step before the account can treat patients.',
    'help.faq.signIn.q': 'I cannot sign in. What should I check?',
    'help.faq.signIn.a':
      'Confirm the email address you registered with and that Caps Lock is off. If two-factor authentication is enabled on your account you will be asked for a 6-digit code after your password; a backup code works in the same field. If sign-in reports that the service is temporarily unavailable, the problem is on our side — please try again shortly.',
    'help.faq.mfa.q': 'How do I turn on two-factor authentication?',
    'help.faq.mfa.a':
      'Open Settings → Security once signed in. You can use an authenticator app, SMS, or email, and you should store the backup codes somewhere safe.',
    'help.faq.records.q': 'How do I add my medical documents?',
    'help.faq.records.a':
      'Go to Medical Records and upload the file. Text is extracted automatically where the document allows it; photographs and scanned pages are stored and viewable but their text cannot be read automatically.',
    'help.faq.appointments.q': 'How do I book or change an appointment?',
    'help.faq.appointments.a':
      'Use Appointments to book with a provider, and the same screen to reschedule or cancel an existing booking.',
    'help.faq.privacy.q': 'Who can see my health data?',
    'help.faq.privacy.a':
      'Only you and the providers you have an active care relationship with. Access is logged. The Privacy Policy sets out exactly what we store and how to request an export or deletion.',
    'help.resources.title': 'Policies and reference',
    'help.resources.privacy': 'Privacy Policy',
    'help.resources.terms': 'Terms of Service',
    'help.resources.cookies': 'Cookie Policy',
    'help.resources.disclaimer': 'Medical Disclaimer',
    'help.stillNeedHelp.title': 'Still need help?',
    'help.stillNeedHelp.body': 'Contact us and we will get back to you.',
    'help.stillNeedHelp.cta': 'Contact Us',

    // Contact (/contact)
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Pick the channel that matches what you need.',
    'contact.emergency.title': 'This is not an emergency service',
    'contact.emergency.body':
      'Tabib is not monitored around the clock. If you or someone else is in a medical emergency, call the ambulance service in Iraq on 121 now.',
    'contact.emergency.cta': 'Call 121',
    'contact.privacy.title': 'Privacy and your data',
    'contact.privacy.body':
      'Data exports, corrections, deletion requests, and any question about how your health information is handled.',
    'contact.legal.title': 'Legal and terms',
    'contact.legal.body':
      'Questions about the Terms of Service, and provider or partnership agreements.',
    'contact.secureMessage.title': 'Clinical questions',
    'contact.secureMessage.body':
      'Sign in and use secure messaging to reach your own care team. Please do not send medical details by email — it is not a protected channel.',
    'contact.secureMessage.cta': 'Open secure messaging',
    'contact.response.title': 'What to expect',
    'contact.response.body':
      'We answer during Iraqi business hours, Sunday to Thursday. Include your account email so we can find your case, but never include a password.',

    // Consult booking — doctor selection (spec 026-c3-consult-doctor-select).
    // The four state messages must stay mutually distinct: a shared string puts
    // the patient back to guessing why the dropdown is empty (FR-008).
    'consult.doctor.loading': 'Loading available doctors...',
    'consult.doctor.error':
      'We could not load the list of doctors. Please check your connection and try again.',
    'consult.doctor.offline':
      'You appear to be offline, so the list of doctors was not requested. Reconnect and try again.',
    'consult.doctor.empty': 'No doctors are currently available for booking.',
    'consult.doctor.retry': 'Try again',
    'consult.doctor.unnamedProvider': 'Doctor (name not on file)',

    // Auth gate (spec 028-h1-route-404). A signed-out visitor at a real,
    // gated route must see this — never the generic "page not found" —
    // and this copy must read identically whether the route exists or not
    // (plan D-4): it must not name the requested path or confirm it exists.
    'authGate.signedOut.title': 'Sign in to continue',
    'authGate.signedOut.body': 'Please sign in to view this page.',
    'authGate.signedOut.signIn': 'Sign in',
    'authGate.unavailable.title': 'Connection problem',
    'authGate.unavailable.body':
      'We could not check your sign-in status. Check your connection and try again.',
    'authGate.unavailable.retry': 'Try again',

    // Consult booking — schedule form (spec 030-h2-h3-i18n, plan §Translation key map).
    // The emergency number itself is NOT in this dictionary: `emergency.body`
    // carries a `{number}` placeholder that the component substitutes, so the
    // tracked 911→121 sweep keeps exactly one greppable site (plan D-4, FR-013).
    'consult.schedule.toast.successTitle': 'Consultation scheduled',
    'consult.schedule.toast.successBody': 'Your video consultation has been booked.',
    'consult.schedule.toast.errorTitle': 'Scheduling failed',
    'consult.schedule.toast.errorBody': 'We could not book your consultation. Please try again.',
    'consult.schedule.toast.missingTitle': 'Missing information',
    'consult.schedule.toast.missingBody': 'Please fill in all required fields.',
    'consult.schedule.toast.pastDateTitle': 'Invalid date',
    'consult.schedule.toast.pastDateBody': 'The consultation time must be in the future.',
    'consult.schedule.success.title': 'Consultation booked!',
    'consult.schedule.success.body':
      'Your video consultation is scheduled. You can review the details in your upcoming sessions.',
    'consult.schedule.success.viewSessions': 'View my sessions',
    'consult.schedule.success.bookAnother': 'Book another consultation',
    'consult.schedule.success.reminderLabel': 'Reminder:',
    'consult.schedule.success.reminderBody':
      'The session link is sent 15 minutes before your appointment. Make sure you have a stable internet connection.',
    'consult.schedule.title': 'Schedule a video consultation',
    'consult.schedule.provider.label': 'Select doctor',
    'consult.schedule.provider.placeholder': 'Choose a doctor',
    'consult.schedule.date.label': 'Consultation date',
    'consult.schedule.date.placeholder': 'Choose a date',
    'consult.schedule.time.label': 'Consultation time',
    'consult.schedule.time.placeholder': 'Choose a suitable time',
    'consult.schedule.time.unavailable': '(unavailable)',
    'consult.schedule.reason.label': 'Reason for consultation',
    'consult.schedule.reason.placeholder':
      'Describe the reason for your consultation and the symptoms you have...',
    'consult.schedule.urgency.label': 'Priority level',
    'consult.schedule.urgency.routine': 'Routine',
    'consult.schedule.urgency.urgent': 'Urgent',
    'consult.schedule.urgency.emergency': 'Emergency',
    'consult.schedule.info.title': 'Session information:',
    'consult.schedule.info.duration': 'Consultation length: 30-60 minutes',
    'consult.schedule.info.link': 'The session link is sent 15 minutes before the appointment',
    'consult.schedule.info.connection': 'Make sure you have a stable internet connection',
    'consult.schedule.info.cancellation': 'You can cancel up to two hours before the appointment',
    'consult.schedule.actions.submitting': 'Booking...',
    'consult.schedule.actions.submit': 'Book consultation',
    'consult.schedule.actions.reset': 'Reset',
    'consult.schedule.emergency.label': 'In an emergency:',
    'consult.schedule.emergency.body': 'call {number} immediately or go to the nearest hospital',
  },
  ar: {
    // Header
    'header.title': 'طبيب',
    'header.subtitle': 'منصة الرعاية الصحية',
    'header.signIn': 'تسجيل الدخول',
    'header.arabic': 'العربية',
    'header.english': 'English',

    // Landing Page
    'landing.hero.title': 'منصة الرعاية الصحية الشاملة في العراق',
    'landing.hero.subtitle':
      'إدارة آمنة للسجلات الطبية وجدولة المواعيد وتنسيق الرعاية الصحية للمرضى والأطباء والمستشفيات والصيدليات في جميع أنحاء العراق.',
    'landing.hero.getStarted': 'ابدأ الآن',

    // Features
    'features.title': 'إدارة رعاية صحية شاملة',
    'features.subtitle':
      'إدارة متقدمة للوثائق الطبية مع رفع آمن وتصنيف وتنسيق سلس عبر مقدمي الرعاية الصحية العراقيين.',

    'features.records.title': 'السجلات الطبية',
    'features.records.description':
      'رفع وتصنيف وإدارة الوثائق الطبية الورقية مع استخراج النص البصري والتخزين السحابي الآمن.',

    'features.appointments.title': 'جدولة المواعيد',
    'features.appointments.description':
      'حجز المواعيد مع مقدمي الرعاية الصحية وتتبع الزيارات القادمة والانضمام إلى الاستشارات المرئية.',

    'features.medications.title': 'إدارة الأدوية',
    'features.medications.description':
      'تتبع الوصفات الطبية ومراقبة معدلات الالتزام والتنسيق مع الصيدليات لتجديد الأدوية.',

    'features.multiUser.title': 'منصة متعددة المستخدمين',
    'features.multiUser.description':
      'لوحات تحكم قائمة على الأدوار للمرضى والأطباء والمستشفيات والصيدليات ومديري الرعاية الصحية.',

    'features.compliance.title': 'الامتثال لمعايير HIPAA',
    'features.compliance.description':
      'أمان على مستوى المؤسسات مع التشفير أثناء الراحة والنقل وسجلات التدقيق وتسجيل الامتثال.',

    'features.monitoring.title': 'مراقبة الصحة',
    'features.monitoring.description':
      'تتبع العلامات الحيوية ونتائج المختبر ومقاييس العافية مع لوحات نظرة عامة صحية قابلة للتوسيع.',

    // User Types
    'userTypes.title': 'مبني لجميع أصحاب المصلحة في الرعاية الصحية',
    'userTypes.subtitle': 'منصة شاملة تخدم النظام البيئي الصحي العراقي بأكمله',
    'userTypes.patients': 'المرضى',
    'userTypes.doctors': 'الأطباء',
    'userTypes.hospitals': 'المستشفيات',
    'userTypes.pharmacies': 'الصيدليات',
    'userTypes.healthServices': 'الخدمات الصحية',
    'userTypes.systemAdmins': 'مديري النظام',

    // CTA
    'cta.title': 'انضم إلى ثورة الرعاية الصحية الرقمية في العراق',
    'cta.subtitle':
      'ابدأ في إدارة سجلاتك الصحية رقمياً بأدوات آمنة وعلى مستوى مهني مصممة لنظام الرعاية الصحية العراقي.',
    'cta.signUp': 'سجل الآن',

    // Footer
    'footer.description': 'منصة الرعاية الصحية الشاملة في العراق لإدارة السجلات الطبية الآمنة.',
    'footer.platform': 'المنصة',
    'footer.platform.dashboard': 'لوحة تحكم المريض',
    'footer.platform.records': 'السجلات الطبية',
    'footer.platform.appointments': 'المواعيد',
    'footer.platform.medications': 'الأدوية',
    'footer.users': 'المستخدمون',
    'footer.support': 'الدعم',
    'footer.support.help': 'مركز المساعدة',
    'footer.support.contact': 'اتصل بنا',
    'footer.support.privacy': 'سياسة الخصوصية',
    'footer.support.terms': 'شروط الخدمة',
    'footer.copyright': '© 2024 منصة طبيب للرعاية الصحية. جميع الحقوق محفوظة.',

    // Dashboard
    'dashboard.welcome': 'مرحباً بعودتك',
    'dashboard.healthOverview': 'نظرة عامة على الصحة',
    'dashboard.viewAll': 'عرض الكل',
    'dashboard.medicalRecords': 'السجلات الطبية',
    'dashboard.appointments': 'المواعيد',
    'dashboard.medications': 'الأدوية',
    'dashboard.noRecords': 'لم يتم رفع سجلات طبية بعد',
    'dashboard.uploadFirst': 'ارفع وثيقتك الطبية الأولى للبدء',
    'dashboard.uploadDocument': 'رفع وثيقة',

    // Health Metrics
    'health.bloodPressure': 'ضغط الدم',
    'health.heartRate': 'معدل نبضات القلب',
    'health.weight': 'الوزن',
    'health.glucose': 'سكر الدم',
    'health.temperature': 'درجة الحرارة',
    'health.oxygen': 'تشبع الأكسجين',
    'health.addMetric': 'إضافة قياس جديد',
    'health.lastUpdated': 'آخر تحديث',
    'health.trend': 'الاتجاه',
    'health.normal': 'طبيعي',
    'health.high': 'مرتفع',
    'health.low': 'منخفض',

    // Medical Records
    'records.title': 'السجلات الطبية',
    'records.uploadNew': 'رفع وثيقة جديدة',
    'records.category': 'الفئة',
    'records.labResults': 'نتائج المختبر',
    'records.xrays': 'الأشعة السينية',
    'records.prescriptions': 'الوصفات الطبية',
    'records.reports': 'التقارير الطبية',
    'records.insurance': 'التأمين',
    'records.other': 'أخرى',
    'records.description': 'الوصف',
    'records.upload': 'رفع',
    'records.dropZone': 'اسحب الملفات هنا أو انقر للرفع',
    'records.supportedFormats': 'الصيغ المدعومة: PDF، JPG، PNG، DICOM (حد أقصى 10 ميجابايت)',
    // TODO(translation): native review pending — non-clinical UI copy
    'records.unreadableText':
      'تعذرت قراءة نص هذه الوثيقة تلقائيًا (يبدو أنها صورة ممسوحة ضوئيًا). لا يستطيع المساعد الذكي الاطلاع على محتواها بعد.',

    // Appointments
    'appointments.title': 'المواعيد',
    'appointments.upcoming': 'القادمة',
    'appointments.past': 'السابقة',
    'appointments.schedule': 'حجز موعد جديد',
    'appointments.with': 'مع',
    'appointments.at': 'في',
    'appointments.status.scheduled': 'مجدول',
    'appointments.status.completed': 'مكتمل',
    'appointments.status.cancelled': 'ملغي',

    // Medications
    'medications.title': 'الأدوية الحالية',
    'medications.add': 'إضافة دواء',
    'medications.name': 'اسم الدواء',
    'medications.dosage': 'الجرعة',
    'medications.frequency': 'التكرار',
    'medications.prescribedBy': 'وصفه الطبيب',
    'medications.adherence': 'الالتزام',
    'medications.taken': 'تم تناوله',
    'medications.missed': 'تم تفويته',

    // Common
    'common.loading': 'جاري التحميل...',
    'common.error': 'خطأ',
    'common.success': 'نجح',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.view': 'عرض',
    'common.download': 'تحميل',
    'common.upload': 'رفع',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.sort': 'ترتيب',
    'common.today': 'اليوم',
    'common.yesterday': 'أمس',
    'common.thisWeek': 'هذا الأسبوع',
    'common.thisMonth': 'هذا الشهر',

    // Book Appointment
    bookAppointment: 'حجز موعد',
    providerType: 'نوع مقدم الخدمة',
    selectProviderType: 'اختر نوع مقدم الخدمة',
    provider: 'مقدم الخدمة',
    selectProvider: 'اختر مقدم الخدمة',
    selectProviderTypeFirst: 'الرجاء اختيار نوع مقدم الخدمة أولاً',
    appointmentDate: 'تاريخ الموعد',
    pickDate: 'اختر تاريخ',
    appointmentTime: 'وقت الموعد',
    selectTime: 'اختر وقت',
    reasonForVisit: 'سبب الزيارة',
    reasonForVisitPlaceholder: 'صف سبب زيارتك بإيجاز',
    additionalNotes: 'ملاحظات إضافية',
    additionalNotesPlaceholder: 'أي معلومات إضافية للطبيب',
    booking: 'جاري الحجز...',
    appointmentBooked: 'تم حجز الموعد',
    appointmentBookedDesc: 'تم جدولة موعدك بنجاح',
    appointmentBookError: 'فشل في حجز الموعد',
    doctor: 'طبيب',
    hospital: 'مستشفى',
    laboratory: 'مختبر',
    healthServices: 'خدمات صحية',
    cancel: 'إلغاء',
    error: 'خطأ',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    actions: 'إجراءات',
    markAsTaken: 'تسجيل الجرعة كمأخوذة',
    doseRecorded: 'تم تسجيل الجرعة',
    doseRecordedDesc: 'تم تحديث نسبة الالتزام',
    medicationDeleted: 'تم حذف الدواء',
    medicationDeletedDesc: 'تمت إزالة الدواء من قائمتك',
    medicationUpdated: 'تم تحديث الدواء',
    medicationUpdatedDesc: 'تم حفظ التغييرات',
    confirmDelete: 'حذف هذا الدواء؟',
    confirmDeleteDesc: 'سيتم حذف الدواء بشكل دائم من قائمتك. يمكنك إضافته لاحقاً.',
    editMedication: 'تعديل الدواء',
    backToMedications: 'العودة إلى الأدوية',
    todayReminders: 'جرعات اليوم',
    expiringSoon: 'ينتهي قريباً',
    activeMeds: 'الأدوية النشطة',
    noMedications: 'لا توجد أدوية بعد',
    noMedicationsDesc: 'اضغط على إضافة لبدء تتبع أدويتك.',
    doctorName: 'اسم الطبيب/مقدم الخدمة',
    doctorNamePlaceholder: 'أدخل اسم الطبيب أو مقدم الخدمة',
    specialty: 'التخصص',
    selectSpecialty: 'اختر التخصص',
    generalMedicine: 'الطب العام',
    cardiology: 'أمراض القلب',
    dermatology: 'الأمراض الجلدية',
    pediatrics: 'طب الأطفال',
    orthopedics: 'جراحة العظام',
    gynecology: 'أمراض النساء والتوليد',
    ophthalmology: 'طب العيون',
    ent: 'أنف وأذن وحنجرة',
    psychiatry: 'الطب النفسي',
    neurology: 'طب الأعصاب',
    appointmentType: 'نوع الموعد',
    selectAppointmentType: 'اختر نوع الموعد',
    inPerson: 'حضوري',
    videoCall: 'مكالمة فيديو',
    phoneCall: 'مكالمة هاتفية',
    reasonAndNotes: 'سبب الزيارة والملاحظات',
    reasonAndNotesPlaceholder: 'صف سبب زيارتك وأي معلومات إضافية',
    reasonAndNotesDescription: 'اذكر الأعراض أو المخاوف أو أي معلومات للطبيب',

    // Patient Profile
    patientProfile: 'الملف الشخصي للمريض',
    managePersonalInfo: 'إدارة معلوماتك الشخصية والطبية',
    completeYourProfile: 'أكمل ملفك الشخصي للبدء',
    editProfile: 'تعديل الملف',
    personalInformation: 'المعلومات الشخصية',
    fullName: 'الاسم الكامل',
    fullNamePlaceholder: 'أدخل اسمك الكامل',
    dateOfBirth: 'تاريخ الميلاد',
    gender: 'الجنس',
    selectGender: 'اختر الجنس',
    male: 'ذكر',
    female: 'أنثى',
    other: 'آخر',
    nationalId: 'رقم الهوية الوطنية',
    preferredLanguage: 'اللغة المفضلة',
    selectLanguage: 'اختر اللغة',
    contactInformation: 'معلومات الاتصال',
    phoneNumber: 'رقم الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    fullAddress: 'أدخل عنوانك الكامل',
    city: 'المدينة',
    postalCode: 'الرمز البريدي',
    optional: 'اختياري',
    emergencyContact: 'جهة الاتصال في حالات الطوارئ',
    contactName: 'اسم جهة الاتصال',
    contactPhone: 'رقم الهاتف',
    relationship: 'العلاقة',
    relationshipPlaceholder: 'مثال: زوج/زوجة، والد/والدة، أخ/أخت',
    medicalInformation: 'المعلومات الطبية',
    bloodType: 'فصيلة الدم',
    selectBloodType: 'اختر فصيلة الدم',
    unknown: 'غير معروف',
    allergies: 'الحساسيات',
    allergiesPlaceholder: 'اذكر أي حساسيات معروفة',
    chronicConditions: 'الأمراض المزمنة',
    chronicConditionsPlaceholder: 'اذكر أي أمراض مزمنة',
    currentMedications: 'الأدوية الحالية',
    medicationsPlaceholder: 'اذكر الأدوية الحالية',
    insuranceInformation: 'معلومات التأمين',
    insuranceProvider: 'شركة التأمين',
    insuranceProviderPlaceholder: 'أدخل اسم شركة التأمين',
    policyNumber: 'رقم الوثيقة',
    policyNumberPlaceholder: 'أدخل رقم الوثيقة',
    saving: 'جاري الحفظ...',
    updateProfile: 'تحديث الملف',
    createProfile: 'إنشاء الملف',
    profileUpdated: 'تم تحديث الملف',
    patientProfileUpdatedDesc: 'تم تحديث ملفك الشخصي بنجاح',
    profileUpdateError: 'فشل في تحديث الملف',

    // Telemedicine
    'telemedicine.title': 'الاستشارات الطبية المرئية',
    'telemedicine.subtitle': 'إدارة جلسات الاستشارة الطبية عن بُعد',
    'telemedicine.bookNew': 'حجز استشارة جديدة',
    'telemedicine.backToDashboard': '← العودة إلى لوحة التحكم',
    'telemedicine.scheduled': 'تم حجز الاستشارة',
    'telemedicine.scheduledDesc': 'تم جدولة الاستشارة الطبية المرئية بنجاح',
    'telemedicine.upcomingSessions': 'الجلسات القادمة',
    'telemedicine.completedSessions': 'الجلسات المكتملة',
    'telemedicine.avgRating': 'متوسط التقييم',
    'telemedicine.avgDuration': 'متوسط المدة',
    'telemedicine.noUpcoming': 'لا توجد جلسات قادمة',
    'telemedicine.noUpcomingPatient': 'لم تقم بحجز أي استشارات طبية بعد',
    'telemedicine.noUpcomingProvider': 'لا توجد استشارات محجوزة معك',
    'telemedicine.noHistory': 'لا يوجد سجل جلسات',
    'telemedicine.noHistoryDesc': 'لم تكمل أي استشارات طبية مرئية بعد',
    'telemedicine.room': 'الغرفة',
    'telemedicine.duration': 'المدة',
    'telemedicine.minutes': 'دقيقة',
    'telemedicine.withDoctor': 'مع الطبيب',
    'telemedicine.withPatient': 'مع المريض',
    'telemedicine.joinSession': 'دخول الجلسة',
    'telemedicine.waiting': 'في الانتظار',
    'telemedicine.details': 'التفاصيل',
    'telemedicine.rating': 'التقييم',
    'telemedicine.status.waiting': 'في الانتظار',
    'telemedicine.status.active': 'نشطة',
    'telemedicine.status.ended': 'انتهت',
    'telemedicine.status.failed': 'فشلت',

    // Medication form (flat keys — match the EN block above; required for
    // EN⟷AR parity. The form references these flat keys directly, not the
    // `medications.*` namespaced versions.)
    addMedication: 'إضافة دواء',
    adding: 'جاري الإضافة...',
    medicationName: 'اسم الدواء',
    medicationNamePlaceholder: 'مثال: ليسينوبريل',
    dosage: 'الجرعة',
    dosagePlaceholder: 'مثال: 10 ملغ',
    dosageHelp: 'أدرج الوحدة (ملغ، مل، وحدة دولية، إلخ)',
    frequency: 'التكرار',
    selectFrequency: 'اختر التكرار',
    route: 'طريقة الإعطاء',
    selectRoute: 'اختر طريقة الإعطاء',
    startDate: 'تاريخ البدء',
    endDate: 'تاريخ الانتهاء',
    endDateOptional: 'اختياري — اتركه فارغًا للأدوية المستمرة',
    prescribedBy: 'وصفه الطبيب',
    prescribedByPlaceholder: 'اسم الطبيب أو مقدم الخدمة',
    purpose: 'الغرض',
    purposePlaceholder: 'مثال: ضغط الدم',
    instructions: 'التعليمات',
    instructionsPlaceholder: 'مثال: يؤخذ مع الطعام',
    sideEffects: 'الأعراض الجانبية',
    sideEffectsPlaceholder: 'اذكر أي أعراض جانبية لاحظتها',
    activeMedication: 'دواء نشط',
    activeMedicationDesc: 'أوقف لإخفاء هذا الدواء من القائمة النشطة',
    medicationAdded: 'تمت إضافة الدواء',
    medicationAddedDesc: 'تمت إضافة الدواء إلى قائمتك',
    medicationAddError: 'فشل في إضافة الدواء',

    // Safety-conflict confirm dialog.
    //   - `body` is the AR string that was already living inline in
    //     `add-medication.tsx` and shipped to production; promoted here
    //     verbatim per operator decision on the i18n branch (treated as
    //     already-reviewed). DO NOT edit without a native review.
    //   - `title`, `confirmButton`, `cancelButton` are NEW strings that
    //     have NOT been native-reviewed; they carry TODO(translation)
    //     markers and currently fall back to the EN copy. They are also
    //     unused at runtime because window.confirm renders only `body`
    //     and browser-native OK/Cancel buttons. Listed in
    //     WORKLOG > Open questions.
    // TODO(translation): native AR review needed before this string is rendered to a user.
    'medications.safetyConflict.title': 'Safety Conflict Detected',
    'medications.safetyConflict.body': 'تم اكتشاف تعارض سلامة خطير. هل تريد المتابعة على أي حال؟',
    // TODO(translation): native AR review needed before this string is rendered to a user.
    'medications.safetyConflict.confirmButton': 'Continue Anyway',
    // TODO(translation): native AR review needed before this string is rendered to a user.
    'medications.safetyConflict.cancelButton': 'Cancel',
    'medications.interactions.coverageDisclaimer':
      'تغطية التفاعلات غير شاملة ومحدودة بمجموعة البيانات الحالية لدينا. لا يُعد هذا الفحص بديلاً عن تقدير الصيدلي أو الطبيب.',
    'medications.interactions.emptyTitle': 'لا توجد تنبيهات في مجموعة البيانات الحالية',
    'medications.interactions.emptyDescription':
      'لم يتم العثور على تفاعلات أو تعارضات حساسية في مجموعة البيانات الحالية لدينا. هذا لا يؤكد أن الدواء آمن.',

    // Frequency options
    onceDaily: 'مرة يومياً',
    twiceDaily: 'مرتين يومياً',
    threeTimesDaily: 'ثلاث مرات يومياً',
    fourTimesDaily: 'أربع مرات يومياً',
    every6Hours: 'كل 6 ساعات',
    every8Hours: 'كل 8 ساعات',
    every12Hours: 'كل 12 ساعة',
    asNeeded: 'عند الحاجة',
    weekly: 'أسبوعياً',
    monthly: 'شهرياً',

    // Route options
    oral: 'فموي',
    injection: 'حقن',
    topical: 'موضعي',
    inhalation: 'استنشاق',
    rectal: 'شرجي',
    sublingual: 'تحت اللسان',
    transdermal: 'عبر الجلد',
    nasal: 'أنفي',
    ophthalmic: 'للعين',
    otic: 'للأذن',

    // Provider search (keys not already defined elsewhere in this block)
    findHealthcareProviders: 'البحث عن مقدمي الرعاية الصحية',
    searchProviderDescription: 'ابحث عن الأطباء والمستشفيات والصيدليات والمختبرات في العراق',
    searchByNameOrSpecialty: 'ابحث بالاسم أو التخصص',
    allTypes: 'جميع الأنواع',
    allCities: 'جميع المدن',
    allSpecializations: 'جميع التخصصات',
    specialization: 'التخصص',
    pharmacy: 'صيدلية',
    acceptsInsurance: 'يقبل التأمين',
    verified: 'موثّق',
    noProvidersFound: 'لم يتم العثور على مقدمي خدمة',
    errorLoadingProviders: 'فشل تحميل مقدمي الخدمة',

    // Shared page chrome
    'common.backHome': 'العودة إلى الصفحة الرئيسية',

    // 404
    'notFound.title': 'تعذّر العثور على هذه الصفحة',
    'notFound.body':
      'قد يكون الرابط قديماً أو ربما كُتب العنوان بصورة مختلفة قليلاً. لم يحدث أي خلل في حسابك.',
    'notFound.home': 'الذهاب إلى الصفحة الرئيسية',
    'notFound.help': 'زيارة مركز المساعدة',

    // Sign-in and registration (/login, /register)
    'auth.login.title': 'مرحباً بعودتك',
    'auth.login.subtitle': 'سجّل الدخول إلى حسابك في طبيب',
    'auth.register.title': 'أنشئ حسابك',
    'auth.register.subtitle': 'انضم إلى طبيب لإدارة سجلاتك الصحية',
    'auth.field.firstName': 'الاسم الأول',
    'auth.field.lastName': 'اسم العائلة',
    'auth.field.email': 'البريد الإلكتروني',
    'auth.field.password': 'كلمة المرور',
    'auth.field.confirmPassword': 'تأكيد كلمة المرور',
    'auth.action.signIn': 'تسجيل الدخول',
    'auth.action.createAccount': 'إنشاء حساب',
    'auth.action.biometric': 'تسجيل الدخول بالبصمة',
    'auth.divider.or': 'أو',
    'auth.prompt.noAccount': 'ليس لديك حساب؟',
    'auth.prompt.createOne': 'أنشئ حساباً',
    'auth.prompt.haveAccount': 'لديك حساب بالفعل؟',
    'auth.error.invalidCredentials': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
    'auth.error.passwordMismatch': 'كلمتا المرور غير متطابقتين',
    'auth.error.consentRequired': 'يجب الموافقة على سياسة الخصوصية وشروط الخدمة للمتابعة',
    'auth.error.registrationFailed': 'تعذّر إنشاء الحساب. يرجى المحاولة مرة أخرى.',
    'auth.error.biometricUnavailable': 'لا توجد بيانات بصمة مسجّلة',
    'auth.error.biometricFailed': 'فشل التحقق بالبصمة',

    // Multi-factor challenge
    'auth.mfa.title': 'تأكيد هويتك',
    'auth.mfa.hint.totp': 'أدخل الرمز المكوّن من ٦ أرقام من تطبيق المصادقة.',
    'auth.mfa.hint.sms': 'أرسلنا رمزاً إلى {phone}.',
    'auth.mfa.hint.smsFallback': 'هاتفك',
    'auth.mfa.hint.email': 'أرسلنا رمزاً إلى بريدك الإلكتروني.',
    'auth.mfa.hint.default': 'أدخل رمز التحقق.',
    'auth.mfa.codePlaceholder': 'أدخل الرمز',
    'auth.mfa.verify': 'تحقق',
    'auth.mfa.resend': 'إعادة إرسال الرمز',
    'auth.mfa.lostAccess':
      'فقدت إمكانية الوصول؟ استخدم أحد رموز النسخ الاحتياطي في الحقل نفسه أعلاه، أو تواصل مع مسؤول النظام.',
    'auth.mfa.error.invalidCode': 'رمز غير صحيح',
    'auth.mfa.error.sendFailed': 'تعذّر إرسال رمز التحقق',

    // Help Center (/help)
    'help.title': 'مركز المساعدة',
    'help.subtitle': 'إجابات عن الأسئلة الأكثر تكراراً، وإلى أين تتوجه بعد ذلك.',
    'help.faq.title': 'الأسئلة الشائعة',
    'help.faq.account.q': 'كيف أنشئ حساباً؟',
    'help.faq.account.a':
      'اختر «ابدأ الآن» في الصفحة الرئيسية للتسجيل كمريض. أما الأطباء والمستشفيات والصيدليات والمختبرات والخدمات الصحية فيسجّلون عبر «انضم كمقدم خدمة»، وتتضمن هذه المسار خطوة للتحقق من الوثائق المهنية قبل أن يتمكن الحساب من تقديم الرعاية.',
    'help.faq.signIn.q': 'لا أستطيع تسجيل الدخول. ما الذي يجب أن أتحقق منه؟',
    'help.faq.signIn.a':
      'تأكد من البريد الإلكتروني الذي سجّلت به ومن أن مفتاح الأحرف الكبيرة غير مفعّل. إذا كان التحقق بخطوتين مفعّلاً على حسابك فسيُطلب منك رمز من ٦ أرقام بعد كلمة المرور، ويمكنك إدخال أحد رموز النسخ الاحتياطي في الحقل نفسه. وإذا ظهرت رسالة بأن الخدمة غير متاحة مؤقتاً فالمشكلة لدينا — يرجى المحاولة بعد قليل.',
    'help.faq.mfa.q': 'كيف أفعّل التحقق بخطوتين؟',
    'help.faq.mfa.a':
      'افتح الإعدادات ← الأمان بعد تسجيل الدخول. يمكنك استخدام تطبيق مصادقة أو رسالة نصية أو البريد الإلكتروني، ويُنصح بحفظ رموز النسخ الاحتياطي في مكان آمن.',
    'help.faq.records.q': 'كيف أضيف وثائقي الطبية؟',
    'help.faq.records.a':
      'انتقل إلى السجلات الطبية وارفع الملف. يُستخرج النص تلقائياً حين تسمح الوثيقة بذلك؛ أما الصور والصفحات الممسوحة ضوئياً فتُحفظ ويمكن عرضها، لكن لا يمكن قراءة نصها تلقائياً.',
    'help.faq.appointments.q': 'كيف أحجز موعداً أو أعدّله؟',
    'help.faq.appointments.a':
      'استخدم صفحة المواعيد للحجز مع مقدم الخدمة، ومن الصفحة نفسها يمكنك إعادة جدولة حجز قائم أو إلغاؤه.',
    'help.faq.privacy.q': 'من يستطيع الاطلاع على بياناتي الصحية؟',
    'help.faq.privacy.a':
      'أنت فقط ومقدمو الرعاية الذين تربطك بهم علاقة رعاية قائمة. ويُسجَّل كل اطلاع على البيانات. وتوضح سياسة الخصوصية ما نحفظه بالضبط وكيفية طلب نسخة من بياناتك أو حذفها.',
    'help.resources.title': 'السياسات والمراجع',
    'help.resources.privacy': 'سياسة الخصوصية',
    'help.resources.terms': 'شروط الخدمة',
    'help.resources.cookies': 'سياسة ملفات تعريف الارتباط',
    'help.resources.disclaimer': 'إخلاء المسؤولية الطبية',
    'help.stillNeedHelp.title': 'ما زلت بحاجة إلى مساعدة؟',
    'help.stillNeedHelp.body': 'تواصل معنا وسنعاود الاتصال بك.',
    'help.stillNeedHelp.cta': 'اتصل بنا',

    // Contact (/contact)
    'contact.title': 'اتصل بنا',
    'contact.subtitle': 'اختر القناة المناسبة لما تحتاجه.',
    'contact.emergency.title': 'هذه ليست خدمة طوارئ',
    'contact.emergency.body':
      'لا تُراقَب منصة طبيب على مدار الساعة. إذا كنت أنت أو شخص آخر في حالة طوارئ طبية، اتصل بالإسعاف في العراق على الرقم ١٢١ الآن.',
    'contact.emergency.cta': 'اتصل بالرقم 121',
    'contact.privacy.title': 'الخصوصية وبياناتك',
    'contact.privacy.body':
      'طلبات تصدير البيانات أو تصحيحها أو حذفها، وأي استفسار عن كيفية التعامل مع معلوماتك الصحية.',
    'contact.legal.title': 'الشؤون القانونية والشروط',
    'contact.legal.body': 'الاستفسارات المتعلقة بشروط الخدمة واتفاقيات مقدمي الخدمة والشراكات.',
    'contact.secureMessage.title': 'الاستفسارات الطبية',
    'contact.secureMessage.body':
      'سجّل الدخول واستخدم المراسلة الآمنة للتواصل مع فريق الرعاية الخاص بك. يرجى عدم إرسال تفاصيل طبية عبر البريد الإلكتروني فهو ليس قناة محمية.',
    'contact.secureMessage.cta': 'فتح المراسلة الآمنة',
    'contact.response.title': 'ما الذي تتوقعه',
    'contact.response.body':
      'نردّ خلال ساعات العمل الرسمية في العراق، من الأحد إلى الخميس. أرفق البريد الإلكتروني المسجَّل في حسابك حتى نتمكن من إيجاد طلبك، ولا ترسل كلمة المرور مطلقاً.',

    // Consult booking — doctor selection (spec 026-c3-consult-doctor-select)
    'consult.doctor.loading': 'جاري تحميل قائمة الأطباء المتاحين...',
    'consult.doctor.error': 'تعذر تحميل قائمة الأطباء. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
    'consult.doctor.offline':
      'يبدو أنك غير متصل بالإنترنت، لذلك لم يتم طلب قائمة الأطباء. أعد الاتصال ثم حاول مرة أخرى.',
    'consult.doctor.empty': 'لا يوجد أطباء متاحون للحجز حالياً.',
    'consult.doctor.retry': 'إعادة المحاولة',
    'consult.doctor.unnamedProvider': 'طبيب (الاسم غير مسجل)',

    // Auth gate (spec 028-h1-route-404)
    'authGate.signedOut.title': 'سجّل الدخول للمتابعة',
    'authGate.signedOut.body': 'يرجى تسجيل الدخول لعرض هذه الصفحة.',
    'authGate.signedOut.signIn': 'تسجيل الدخول',
    'authGate.unavailable.title': 'مشكلة في الاتصال',
    'authGate.unavailable.body': 'تعذر التحقق من حالة تسجيل دخولك. تحقق من اتصالك وحاول مرة أخرى.',
    'authGate.unavailable.retry': 'إعادة المحاولة',

    // Consult booking — schedule form (spec 030-h2-h3-i18n).
    'consult.schedule.toast.successTitle': 'تم جدولة الجلسة بنجاح',
    'consult.schedule.toast.successBody': 'تم حجز موعد الاستشارة الطبية المرئية',
    'consult.schedule.toast.errorTitle': 'خطأ في الجدولة',
    'consult.schedule.toast.errorBody': 'فشل في حجز موعد الاستشارة الطبية',
    'consult.schedule.toast.missingTitle': 'بيانات ناقصة',
    'consult.schedule.toast.missingBody': 'يرجى ملء جميع الحقول المطلوبة',
    'consult.schedule.toast.pastDateTitle': 'تاريخ غير صالح',
    'consult.schedule.toast.pastDateBody': 'يجب أن يكون موعد الاستشارة في المستقبل',
    'consult.schedule.success.title': 'تم حجز الاستشارة بنجاح!',
    'consult.schedule.success.body':
      'تم جدولة موعد الاستشارة الطبية المرئية. يمكنك مراجعة تفاصيل الموعد في قائمة الجلسات القادمة.',
    'consult.schedule.success.viewSessions': 'عرض جلساتي',
    'consult.schedule.success.bookAnother': 'حجز استشارة أخرى',
    'consult.schedule.success.reminderLabel': 'تذكير:',
    'consult.schedule.success.reminderBody':
      'سيتم إرسال رابط الجلسة قبل الموعد بـ 15 دقيقة. تأكد من وجود اتصال إنترنت مستقر.',
    'consult.schedule.title': 'جدولة استشارة طبية مرئية',
    'consult.schedule.provider.label': 'اختيار الطبيب',
    'consult.schedule.provider.placeholder': 'اختر الطبيب المطلوب',
    'consult.schedule.date.label': 'تاريخ الاستشارة',
    'consult.schedule.date.placeholder': 'اختر التاريخ',
    'consult.schedule.time.label': 'وقت الاستشارة',
    'consult.schedule.time.placeholder': 'اختر الوقت المناسب',
    'consult.schedule.time.unavailable': '(غير متاح)',
    'consult.schedule.reason.label': 'سبب الاستشارة',
    'consult.schedule.reason.placeholder': 'اكتب سبب الاستشارة والأعراض التي تعاني منها...',
    'consult.schedule.urgency.label': 'مستوى الأولوية',
    'consult.schedule.urgency.routine': 'عادية',
    'consult.schedule.urgency.urgent': 'عاجلة',
    'consult.schedule.urgency.emergency': 'طارئة',
    'consult.schedule.info.title': 'معلومات الجلسة:',
    'consult.schedule.info.duration': 'مدة الاستشارة: 30-60 دقيقة',
    'consult.schedule.info.link': 'يتم إرسال رابط الجلسة قبل الموعد بـ 15 دقيقة',
    'consult.schedule.info.connection': 'تأكد من وجود اتصال إنترنت مستقر',
    'consult.schedule.info.cancellation': 'يمكن إلغاء الموعد قبل ساعتين من الموعد المحدد',
    'consult.schedule.actions.submitting': 'جاري الحجز...',
    'consult.schedule.actions.submit': 'حجز الاستشارة',
    'consult.schedule.actions.reset': 'إعادة تعيين',
    'consult.schedule.emergency.label': 'في حالات الطوارئ:',
    // 121 is the owner-confirmed Iraq medical emergency number — the {number}
    // placeholder is substituted at render time, never a raw literal here.
    'consult.schedule.emergency.body': 'اتصل فوراً بالرقم {number} أو توجه إلى أقرب مستشفى',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');
  const [_isInitialized, setIsInitialized] = useState(false);

  // Load language preference from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tabib-language') as Language;
      if (saved && (saved === 'en' || saved === 'ar')) {
        setLanguage(saved);
      }
      setIsInitialized(true);
    }
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'ar' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('tabib-language', newLanguage);
  };

  const t = (key: string): string => {
    // Bracket-indexed lookup with explicit hasOwnProperty guard to satisfy
    // eslint security/detect-object-injection. Fixed branch on `language`
    // avoids dynamic `translations[language]` access.
    const dict: Record<string, string> = language === 'ar' ? translations.ar : translations.en;
    return Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : key;
  };

  const isRTL = language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
